import { useEffect } from 'react';
import { HelpCircle, LogOut, PanelLeft, Plus } from 'lucide-react';
import { useI18nContext } from '@/i18n/i18n-react';
import { LanguageSelector } from '@/components/language-selector';
import { FoundersOnlyPromoCard } from '@/components/founders-only/founders-only-promo-card';
import { TenantSwitcher } from '@/components/tenant-switcher';
import korzaLogoBlackBlue from '@/assets/korza_logo_black_blue.png';
import korzaLogoBlack from '@/assets/korza_logo_black.png';
import { Link, useLocation } from 'react-router';
import { useTheme } from '@/hooks/use-theme';
import { MenuTag } from './menu-tag';
import { useTenant } from '@/hooks/use-tenant';
import { useChatwootWidget } from './chatwoot-widget/chatwoot-widget-context';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useActiveSubscription } from '@/hooks/use-billing';
import { useLogout } from '@/hooks/use-logout';
import { getNavItems, isNavItemActive, type NavItem } from '@/lib/navigation';
import { NAV_ITEM_ICONS, useNavItemLabel } from './shell/nav-items';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

/**
 * Desktop sidebar (SHELL-06): same destinations and names as the mobile shell
 * (AD-016), with today's look (AC 6-4).
 */
export function AppSidebar() {
  const pathname = useLocation().pathname;
  const theme = useTheme();
  const { LL } = useI18nContext();
  const labelOf = useNavItemLabel();
  const tenant = useTenant();
  const { setIsOpen: openHelp, setHasShellOnScreen } = useChatwootWidget();
  const { data: featureFlags } = useFeatureFlags();
  const { data: subscription } = useActiveSubscription();
  const logout = useLogout();
  const { state, toggleSidebar } = useSidebar();

  const items = getNavItems({
    tenantSlug: tenant.slug,
    audiencesEnabled: featureFlags?.audiencesEnabled ?? false,
    hasActiveSubscription: !!subscription,
    fromShopifyAppStore: tenant.fromShopifyAppStore,
  });
  const primaryItems = items.filter((i) => i.group === 'primary');
  const storeItems = items.filter((i) => i.group === 'store');

  const collapseLabel =
    state === 'expanded'
      ? LL.shell.sidebar.collapse()
      : LL.shell.sidebar.expand();

  useEffect(() => {
    setHasShellOnScreen(true);
    return () => setHasShellOnScreen(false);
  }, [setHasShellOnScreen]);

  const renderItem = (item: NavItem) => {
    const Icon = NAV_ITEM_ICONS[item.id];
    const label = labelOf(item.id);
    return (
      <SidebarMenuItem key={item.id}>
        <SidebarMenuButton
          asChild
          isActive={isNavItemActive(item.href, pathname)}
          tooltip={label}
          className="pl-6"
        >
          <Link to={item.href}>
            <Icon />
            <span>{label}</span>
            {item.soonTag && (
              <MenuTag text={LL.common.soon()} color="bg-blue-500" />
            )}
          </Link>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="static h-full">
      <SidebarHeader>
        <div className="flex items-center justify-between pt-6 pl-4 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:pl-0">
          <img
            alt="Korza logo"
            src={
              theme.systemTheme === 'light'
                ? korzaLogoBlack
                : korzaLogoBlackBlue
            }
            className="max-h-5 w-auto group-data-[collapsible=icon]:hidden"
          />
          <TenantSwitcher />
        </div>
      </SidebarHeader>

      <SidebarContent className="pt-8">
        <SidebarMenu className="px-4 group-data-[collapsible=icon]:px-2">
          <SidebarMenuItem>
            <SidebarMenuButton
              asChild
              tooltip={LL.shell.newCampaign()}
              className="bg-primary font-medium text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground active:bg-primary/90 active:text-primary-foreground"
            >
              <Link to={`/${tenant.slug}/campaigns/create`}>
                <Plus />
                <span>{LL.shell.newCampaign()}</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>

        <SidebarMenu>{primaryItems.map(renderItem)}</SidebarMenu>

        <SidebarGroup className="p-0">
          <SidebarGroupLabel className="pl-6">
            {LL.shell.sidebar.storeGroup()}
          </SidebarGroupLabel>
          <SidebarMenu>{storeItems.map(renderItem)}</SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <FoundersOnlyPromoCard />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => openHelp(true)}
              tooltip={LL.helpPage.title()}
            >
              <HelpCircle />
              <span>{LL.helpPage.title()}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem className="group-data-[collapsible=icon]:hidden">
            <LanguageSelector />
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton
              onClick={() => void logout()}
              tooltip={LL.logout()}
            >
              <LogOut />
              <span>{LL.logout()}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          <SidebarMenuItem>
            <SidebarMenuButton onClick={toggleSidebar} tooltip={collapseLabel}>
              <PanelLeft />
              <span>{collapseLabel}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
